import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Assessment,
  Speed,
} from "@mui/icons-material";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const Analytics = () => {
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(false);

  const numberLocale = (() => {
    const l = String(i18n.language || "").toLowerCase();
    if (l.startsWith("pt")) return "pt-BR";
    if (l.startsWith("en")) return "en-US";
    if (l.startsWith("fr")) return "fr-FR";
    if (l.startsWith("it")) return "it-IT";
    return "es-ES";
  })();

  const formatCurrency = (value) => {
    const numeric = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numeric)) return String(value ?? "");
    try {
      return new Intl.NumberFormat(numberLocale, { style: "currency", currency: "BRL" }).format(numeric);
    } catch (_) {
      return String(value ?? "");
    }
  };

  const [analyticsData, setAnalyticsData] = useState({
    metrics: {
      totalRevenue: 0,
      revenueGrowth: 0,
      totalLeads: 0,
      leadsGrowth: 0,
      conversionRate: 0,
      conversionGrowth: 0,
      avgDealSize: 0,
      avgDealGrowth: 0,
    },
    topProducts: [],
    leadsSource: [],
    monthlyTrends: [],
  });

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch real data from multiple endpoints
      const [metricsRes, leadsRes, salesRes] = await Promise.all([
        api.get(`/analytics/metrics?period=${period}`).catch(() => ({ data: {} })),
        api.get('/leads').catch(() => ({ data: { leads: [] } })),
        api.get('/dashboard/sales-summary').catch(() => ({ data: {} }))
      ]);

      const metrics = metricsRes.data || {};
      const leads = leadsRes.data?.leads || [];
      const sales = salesRes.data || {};

      // Calculate metrics from real data
      const totalLeads = leads.length;
      const convertedLeads = leads.filter(l => l.status === 'converted' || l.status === 'won').length;
      const conversionRate = totalLeads > 0 ? ((convertedLeads / totalLeads) * 100).toFixed(1) : 0;
      const totalRevenue = sales.totalSales || leads.reduce((sum, l) => sum + (l.value || 0), 0);
      const avgDealSize = convertedLeads > 0 ? Math.round(totalRevenue / convertedLeads) : 0;

      // Calculate lead sources
      const sourceGroups = leads.reduce((acc, lead) => {
        const source = lead.source || i18n.t("common.other");
        acc[source] = (acc[source] || 0) + 1;
        return acc;
      }, {});
      const leadsSource = Object.entries(sourceGroups).map(([source, count]) => ({
        source,
        count,
        percentage: totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0
      })).sort((a, b) => b.count - a.count).slice(0, 4);

      setAnalyticsData({
        metrics: {
          totalRevenue: metrics.totalRevenue || totalRevenue,
          revenueGrowth: metrics.revenueGrowth || 0,
          totalLeads,
          leadsGrowth: metrics.leadsGrowth || 0,
          conversionRate: parseFloat(conversionRate),
          conversionGrowth: metrics.conversionGrowth || 0,
          avgDealSize,
          avgDealGrowth: metrics.avgDealGrowth || 0,
        },
        topProducts: metrics.topProducts || [],
        leadsSource,
        monthlyTrends: metrics.monthlyTrends || [],
      });

    } catch (error) {
      toast.error(i18n.t("analytics.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, growth, icon: Icon, color }) => {
    const isPositive = growth >= 0;
    return (
      <Card
        sx={{
          height: "100%",
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          borderLeft: `4px solid ${color}`,
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography color="textSecondary" variant="body2" fontWeight={600}>
              {title}
            </Typography>
            <Icon sx={{ color, fontSize: 28 }} />
          </Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1, color }}>
            {value}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 16, color: "#10b981", mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: "#ef4444", mr: 0.5 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: isPositive ? "#10b981" : "#ef4444",
                fontWeight: 600,
              }}
            >
              {i18n.t("analytics.metrics.vsPreviousPeriod", { value: Math.abs(growth) })}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Paper
      sx={{
        p: 3,
        minHeight: "calc(100vh - 100px)",
        background: "linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%)",
      }}
      elevation={0}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {i18n.t("analytics.title")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {i18n.t("analytics.subtitle")}
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>{i18n.t("analytics.period.label")}</InputLabel>
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            label={i18n.t("analytics.period.label")}
          >
            <MenuItem value="week">{i18n.t("analytics.period.options.week")}</MenuItem>
            <MenuItem value="month">{i18n.t("analytics.period.options.month")}</MenuItem>
            <MenuItem value="quarter">{i18n.t("analytics.period.options.quarter")}</MenuItem>
            <MenuItem value="year">{i18n.t("analytics.period.options.year")}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={i18n.t("analytics.metrics.totalRevenue")}
            value={formatCurrency(analyticsData.metrics.totalRevenue)}
            growth={analyticsData.metrics.revenueGrowth}
            icon={AttachMoney}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={i18n.t("analytics.metrics.totalLeads")}
            value={analyticsData.metrics.totalLeads}
            growth={analyticsData.metrics.leadsGrowth}
            icon={People}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={i18n.t("analytics.metrics.conversionRate")}
            value={`${analyticsData.metrics.conversionRate}%`}
            growth={analyticsData.metrics.conversionGrowth}
            icon={Assessment}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title={i18n.t("analytics.metrics.avgDealSize")}
            value={formatCurrency(analyticsData.metrics.avgDealSize)}
            growth={analyticsData.metrics.avgDealGrowth}
            icon={Speed}
            color="#ec4899"
          />
        </Grid>
      </Grid>

      {/* Charts and Tables */}
      <Grid container spacing={3}>
        {/* Monthly Trends */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("analytics.monthlyTrends.title")}
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: "#f8f9fa" }}>
                    <TableRow>
                      <TableCell>
                        <strong>{i18n.t("analytics.monthlyTrends.table.month")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("analytics.monthlyTrends.table.leads")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("analytics.monthlyTrends.table.conversions")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("analytics.monthlyTrends.table.conversionRate")}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{i18n.t("analytics.monthlyTrends.table.revenue")}</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.monthlyTrends.map((trend, index) => (
                      <TableRow
                        key={index}
                        hover
                        sx={{
                          backgroundColor:
                            index === analyticsData.monthlyTrends.length - 1
                              ? "#f0fdf4"
                              : "transparent",
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {trend.month}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{trend.leads}</TableCell>
                        <TableCell align="right">{trend.conversions}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${(
                              (trend.conversions / trend.leads) *
                              100
                            ).toFixed(1)}%`}
                            size="small"
                            sx={{
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(trend.revenue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Simple Bar Chart Representation */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  {i18n.t("analytics.monthlyTrends.revenueTrend")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, mt: 2 }}>
                  {analyticsData.monthlyTrends.map((trend, index) => {
                    const maxRevenue = Math.max(
                      ...analyticsData.monthlyTrends.map((t) => t.revenue)
                    );
                    const height = (trend.revenue / maxRevenue) * 150;
                    return (
                      <Box
                        key={index}
                        sx={{
                          flex: 1,
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            height: `${height}px`,
                            background:
                              "linear-gradient(to top, #6366f1, #8b5cf6)",
                            borderRadius: "4px 4px 0 0",
                            transition: "all 0.3s",
                            "&:hover": {
                              opacity: 0.8,
                            },
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {trend.month}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products & Lead Sources */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("analytics.topProducts.title")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {analyticsData.topProducts.map((product, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {product.name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("analytics.topProducts.salesLabel", { count: product.sales })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "#e5e7eb",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${product.percentage}%`,
                            height: "100%",
                            borderRadius: 4,
                            background:
                              index === 0
                                ? "linear-gradient(90deg, #10b981, #059669)"
                                : index === 1
                                ? "linear-gradient(90deg, #6366f1, #8b5cf6)"
                                : "linear-gradient(90deg, #f59e0b, #fb923c)",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {product.percentage}%
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {formatCurrency(product.revenue)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {i18n.t("analytics.leadSources.title")}
              </Typography>
              <Box sx={{ mt: 2 }}>
                {analyticsData.leadsSource.map((source, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {source.source}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {i18n.t("analytics.leadSources.countLabel", { count: source.count })}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          backgroundColor: "#e5e7eb",
                        }}
                      >
                        <Box
                          sx={{
                            width: `${source.percentage}%`,
                            height: "100%",
                            borderRadius: 4,
                            background:
                              "linear-gradient(90deg, #ec4899, #db2777)",
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {source.percentage}%
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default Analytics;
